import { type AssetRelationType, type OwnershipLevel } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useLocalSearchParams } from 'expo-router'
import { useEffect, useMemo, useState } from 'react'
import { PropertyListingView } from '~/components/assets/PropertyListingView'
import ContentBox from '~/components/layout/ContentBox'
import LeadsView from '~/components/leads/LeadsView'
import { TabBar, TabContent, Tabs, useSetTabCount, type TabItem } from '~/components/ui/tabs'
import { cn } from '~/lib/utils'

type BrowseTab = 'leads' | 'assets'

function LeadsBrowseTab({ active, count }: { active: boolean; count: number }) {
  useSetTabCount('leads', count || undefined)

  const { leadsType } = useLocalSearchParams<{ leadsType?: OwnershipLevel }>()

  if (!active) {
    return null
  }

  return (
    <ContentBox
      scrollable={false}
      variant="default"
      className="rounded-tl-none"
      contentContainerClassName="px-0 py-6">
      <LeadsView initialTab={leadsType} />
    </ContentBox>
  )
}

function AssetsBrowseTab({ active, count }: { active: boolean; count: number }) {
  useSetTabCount('assets', count || undefined)

  const { assetsType } = useLocalSearchParams<{ assetsType?: AssetRelationType }>()

  if (!active) {
    return null
  }

  return (
    <ContentBox
      scrollable={false}
      variant="default"
      className="rounded-tr-none"
      contentContainerClassName="px-0 py-6">
      <PropertyListingView relationType={assetsType} />
    </ContentBox>
  )
}

export function HomeBrowseTabs({
  assetCount,
  initialView,
  leadCount,
}: {
  assetCount: number
  initialView?: 'leads' | 'assets'
  leadCount: number
}) {
  const { t } = useTranslations('feed')
  const [activeTab, setActiveTab] = useState<BrowseTab>(initialView ?? 'leads')

  useEffect(() => {
    if (initialView) {
      setActiveTab(initialView)
    }
  }, [initialView])

  const tabs = useMemo((): TabItem<BrowseTab>[] => {
    return [
      {
        id: 'leads',
        label: t(($) => $.tabs.leads),
        animationOrigin: 'top-left',
        content: ({ active }) => <LeadsBrowseTab active={active} count={leadCount} />,
      },
      {
        id: 'assets',
        label: t(($) => $.tabs.assets),
        animationOrigin: 'top-right',
        content: ({ active }) => <AssetsBrowseTab active={active} count={assetCount} />,
      },
    ]
  }, [assetCount, leadCount, t])

  return (
    <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab}>
      <TabBar tabButtonTextClassName={cn('font-normal text-base/relaxed sm:text-lg/relaxed')} />
      <TabContent tabContentClassName="gap-3" />
    </Tabs>
  )
}
